import { Box, Stack, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { FormControlItem } from '@app/components/Catalog/FormControlItem';
import { CitySelect } from '@app/components/Shared/CitySelect';
import { PrefectureSelect } from '@app/components/Shared/PrefectureSelect';
import { Organization } from '@app/types/organization';
import { isFullWorkLocationDetail } from '@app/utils/productOrganization';

/** 「勤務地情報」「アクセス情報」が操作するフォーム項目 */
type WorkLocationFieldName =
  | 'access'
  | 'workAddress1'
  | 'workAddress2'
  | 'workCity'
  | 'workLocationOther'
  | 'workPostalCode'
  | 'workPrefecture';

interface WorkLocationFieldsProps<T extends FieldValues> {
  control: Control<T>;
  organization: Organization;
  setValue: UseFormSetValue<T>;
  /** creation:新規登録・編集画面 / bulk:一括編集画面 */
  variant: 'creation' | 'bulk';
  watch: UseFormWatch<T>;
}

/**
 * 求人の勤務地情報(郵便番号・都道府県・市区町村・町名・番地・建物名)とアクセス情報の入力欄。
 * NOTE:新規登録・編集画面と一括編集画面でレイアウトのみ異なるためvariantで切り替える
 */
export function WorkLocationFields<T extends FieldValues>({
  control,
  organization,
  setValue,
  variant,
  watch,
}: WorkLocationFieldsProps<T>) {
  const theme = useTheme();
  const shouldShowFullWorkLocation = isFullWorkLocationDetail(organization);
  const isBulk = variant === 'bulk';
  // NOTE:呼び出し元のフォーム型は異なるが、いずれもWorkLocationFieldNameの項目を持つ前提でキャストする
  const path = (name: WorkLocationFieldName) => name as Path<T>;
  const isDisabled = !watch(path('workLocationOther'));
  const whiteBackground = { sx: { bgcolor: theme.palette.common.white } };
  /** 一括編集画面のみ幅を固定する */
  const textWidth = isBulk ? { width: '25rem' } : undefined;
  const selectWidth = isBulk ? { width: '12rem' } : undefined;
  const isFullWidth = !isBulk;
  /** 入力済みの値が非活性でも読めるようにする */
  const filledTextColor = (value: unknown, emptyColor?: string) => ({
    '.Mui-disabled': {
      WebkitTextFillColor: value
        ? `${theme.palette.text.primary} !important`
        : emptyColor,
    },
  });
  const requiredLabel = (title: string) => (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Typography>{title}</Typography>
      <Typography color={theme.customPalette.alert}>*</Typography>
    </Box>
  );

  const postalCodeField = (
    <Controller
      name={path('workPostalCode')}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          disabled={isDisabled}
          ref={isBulk ? undefined : field.ref}
          inputRef={field.ref} // NOTE:バリデーション時にフォーカスされる
          InputProps={whiteBackground}
          placeholder="郵便番号を入力"
          error={!!error}
          helperText={error?.message}
          sx={{ width: '12rem' }}
        />
      )}
    />
  );

  const prefectureField = (
    <Controller
      name={path('workPrefecture')}
      control={control}
      rules={{ required: '都道府県を選択してください' }}
      render={({ field, fieldState: { error } }) => (
        <PrefectureSelect
          {...field}
          disabled={isDisabled}
          ref={isBulk ? undefined : field.ref}
          inputRef={field.ref}
          fullWidth={isFullWidth}
          sx={{
            ...filledTextColor(watch(path('workPrefecture'))),
            ...selectWidth,
          }}
          inputProps={isBulk ? whiteBackground : undefined}
          onChange={(e) => {
            setValue(path('workCity'), '' as PathValue<T, Path<T>>);
            field.onChange(e);
          }}
          error={!!error}
          helperText={error?.message}
        />
      )}
    />
  );

  const cityField = (
    <Controller
      name={path('workCity')}
      control={control}
      rules={{ required: '市町村を選択してください' }}
      render={({ field, fieldState: { error } }) => (
        <CitySelect
          {...field}
          disabled={isDisabled}
          ref={isBulk ? undefined : field.ref}
          inputRef={field.ref}
          fullWidth={isFullWidth}
          sx={{ ...filledTextColor(watch(path('workCity'))), ...selectWidth }}
          inputProps={isBulk ? whiteBackground : undefined}
          prefecture={watch(path('workPrefecture')) as string}
          error={!!error}
          helperText={error?.message}
        />
      )}
    />
  );

  const addressField = (
    name: 'workAddress1' | 'workAddress2',
    placeholder: string
  ) => (
    <Controller
      name={path(name)}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          disabled={isDisabled}
          ref={name === 'workAddress2' || isBulk ? undefined : field.ref}
          inputRef={field.ref}
          fullWidth={isFullWidth}
          sx={textWidth}
          InputProps={isBulk ? whiteBackground : undefined}
          error={!!error}
          helperText={error?.message}
          placeholder={placeholder}
        />
      )}
    />
  );

  const accessField = (
    <Controller
      name={path('access')}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          disabled={isDisabled}
          ref={isBulk ? undefined : field.ref}
          inputRef={field.ref}
          error={!!error}
          multiline
          minRows={isBulk ? undefined : 2}
          rows={isBulk ? 2.5 : undefined}
          fullWidth={isFullWidth}
          sx={{
            ...filledTextColor(
              watch(path('access')),
              `${theme.palette.grey[800]} !important`
            ),
            ...textWidth,
          }}
          InputProps={isBulk ? whiteBackground : undefined}
          placeholder={`アクセス情報を入力\r例）ＪＲ山手線新宿駅から徒歩５分`}
          helperText={error?.message}
        />
      )}
    />
  );

  const detailFields = shouldShowFullWorkLocation && (
    <>
      {addressField('workAddress1', '町名・番地を入力')}
      {addressField('workAddress2', '建物名・部屋番号を入力')}
    </>
  );

  if (isBulk) {
    return (
      <>
        {requiredLabel('勤務地情報')}
        <Box display="flex" flexDirection="column" gap={1}>
          {postalCodeField}
          <Box display="flex" alignItems="center" gap={2} width="25rem">
            {prefectureField}
            {cityField}
          </Box>
          {/* NOTE:組織区分によっては勤務地の詳細を掲載しないため表示しない */}
          {shouldShowFullWorkLocation && (
            <>
              {detailFields}
              {requiredLabel('アクセス情報')}
              {accessField}
            </>
          )}
        </Box>
      </>
    );
  }

  return (
    <>
      <FormControlItem title="勤務地情報" required>
        <Stack spacing={1}>
          {postalCodeField}
          <Stack direction="row" spacing={2}>
            {prefectureField}
            {cityField}
          </Stack>
          {/* NOTE:組織区分によっては勤務地の詳細を掲載しないため表示しない */}
          {detailFields}
        </Stack>
      </FormControlItem>
      {shouldShowFullWorkLocation && (
        <FormControlItem title="アクセス情報" required>
          {accessField}
        </FormControlItem>
      )}
    </>
  );
}
